import { Injectable, Inject, Optional, signal, computed } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import { S3_CONFIG_SERVICE_CONFIG, S3ConfigServiceConfig } from './s3config.config';

export interface AppConfig {
  locationProvider: 'radar' | 'google';
}

type AppConfigMap = Record<string, AppConfig>;

@Injectable({
  providedIn: 'root'
})
export class S3ConfigService {
  private configUrl: string;
  private appName: string;
  private readonly config = signal<AppConfig | null>(null);
  readonly configLoaded = computed(() => this.config() !== null);
  private readonly config$: Observable<AppConfig | null>;
  private readonly FALLBACK_CONFIG: AppConfig = { locationProvider: 'radar' };

  private readonly REQUEST_TIMEOUT = 2000; // 2 seconds

  constructor(
    private http: HttpClient,
    @Optional() @Inject(S3_CONFIG_SERVICE_CONFIG) config: S3ConfigServiceConfig | null
  ) {
    this.configUrl = config?.s3ConfigUrl ?? '';
    this.appName = config?.appName ?? '';
    // Create observable in constructor (injection context)
    this.config$ = toObservable(this.config);
  }

  /**
   * Load configuration from S3 with timeout
   * Falls back to default (radar) if request fails or times out
   */
  loadConfig(): Observable<AppConfig> {
    if (!this.configUrl) {
      this.config.set(this.FALLBACK_CONFIG);
      return of(this.FALLBACK_CONFIG);
    }

    return this.http.get<unknown>(this.configUrl).pipe(
      timeout(this.REQUEST_TIMEOUT), // Fail after 2 seconds
      map(rawConfig => this.resolveConfig(rawConfig)),
      tap(config => this.config.set(config)),
      catchError(() => {
        this.config.set(this.FALLBACK_CONFIG);
        return of(this.FALLBACK_CONFIG);
      })
    );
  }

  /**
   * Resolve app config from either:
   * - legacy format: { locationProvider: 'google' }
   * - app-keyed format: { default: { ... }, 'web-consumer': { ... } }
   */
  private resolveConfig(rawConfig: unknown): AppConfig {
    if (this.isAppConfig(rawConfig)) {
      return rawConfig;
    }

    if (rawConfig && typeof rawConfig === 'object') {
      const configMap = rawConfig as AppConfigMap;

      if (this.appName && this.isAppConfig(configMap[this.appName])) {
        return configMap[this.appName];
      }

      if (this.isAppConfig(configMap['default'])) {
        return configMap['default'];
      }
    }

    return this.FALLBACK_CONFIG;
  }

  private isAppConfig(config: unknown): config is AppConfig {
    if (!config || typeof config !== 'object') {
      return false;
    }

    const locationProvider = (config as AppConfig).locationProvider;
    return locationProvider === 'radar' || locationProvider === 'google';
  }

  /**
   * Get configuration as observable
   * Subscribe to this to react to config changes
   */
  getConfig(): Observable<AppConfig | null> {
    return this.config$;
  }

  /**
   * Get current configuration synchronously
   * Returns null if config hasn't been loaded yet
   */
  getCurrentConfig(): AppConfig | null {
    return this.config();
  }

  /**
   * Get the current location provider
   * Returns 'radar' as default if config not loaded
   */
  getLocationProvider(): 'radar' | 'google' {
    return this.config()?.locationProvider ?? this.FALLBACK_CONFIG.locationProvider;
  }

  /**
   * Check if configuration has been loaded
   */
  isConfigLoaded(): boolean {
    return this.configLoaded();
  }

  /**
   * Manually update the location provider
   * Useful for testing or manual overrides
   */
  updateLocationProvider(provider: 'radar' | 'google'): void {
    this.config.update(current =>
      current ? { ...current, locationProvider: provider } : null
    );
  }
}
